import React, { useEffect, useMemo, useRef, useState } from 'react';
import pluralize from 'pluralize';
import {
  useLoaderData,
  useNavigate,
} from '@tanstack/react-router';
import { usePromiseModal } from '@prezly/react-promise-modal';
import { toast } from 'sonner';

import { APIbulkUpdateSprkImages, APIfetchAssets } from '../../../api/Assets';
import { getCurrentPage } from '../../../helpers';

import Pagination from '../../Pagination';
import PaginationHeader from '../../PaginationHeader';
import DashboardAdminAsset from './Asset';
import AssetDeleteModal from './AssetDeleteModal';
import AssetEditModal from './AssetEditModal';
import AssetGroupsBulkModal from './AssetGroupsBulkModal';

import type WaCheckbox from '@awesome.me/webawesome/dist/components/checkbox/checkbox.js';
import type { Asset } from '../../../types/asset';
import type { AssetGroup } from '../../../types/asset-group';

import '@awesome.me/webawesome/dist/components/checkbox/checkbox.js';
import '@awesome.me/webawesome/dist/components/divider/divider.js';
import '@awesome.me/webawesome/dist/components/dropdown/dropdown.js';
import '@awesome.me/webawesome/dist/components/dropdown-item/dropdown-item.js';

type BulkAction = 'groups' | 'hide' | 'unhide' | 'delete';

const DashboardAdminAssets: React.FC = () => {
  const { images: loadedImagesData, assetGroups } = useLoaderData({ strict: false });
  const [imagesData, setImagesData] = useState(loadedImagesData);
  const images: Asset[] = useMemo(() => imagesData?.data || [], [imagesData]);
  const totalImages = imagesData?.meta.total;
  const perPage = imagesData?.meta.per_page;

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [isBulkUpdating, setIsBulkUpdating] = useState(false);
  const bulkDropdownRef = useRef<HTMLElement>(null);
  const selectedImages = images.filter(image => selectedIds.includes(image.id));
  const isAllSelected = images.length > 0 && selectedImages.length === images.length;

  const navigate = useNavigate();

  useEffect(() => {
    setImagesData(loadedImagesData);
  }, [loadedImagesData]);

  const refreshImages = async () => {
    try {
      setImagesData(await APIfetchAssets({ shared: true, in_admin: true, page: getCurrentPage() }));
    } catch (error) {
      toast.error((error as Error)?.message || 'Failed to refresh images');
    }
  };

  useEffect(() => {
    setSelectedIds(ids => ids.filter(id => images.some(image => image.id === id)));
  }, [images]);

  const editAsset = usePromiseModal<boolean, { asset: Asset; assetGroups: AssetGroup[] }>(
    ({ show, onSubmit, onDismiss, asset, assetGroups }) => (
      <AssetEditModal
        asset={asset}
        assetGroups={assetGroups}
        show={show}
        onConfirm={() => onSubmit(true)}
        onDismiss={onDismiss}
      />
    )
  );

  const deleteAssets = usePromiseModal<boolean, { assets: Asset[] }>(
    ({ show, onSubmit, onDismiss, assets }) => (
      <AssetDeleteModal
        assets={assets}
        show={show}
        onConfirm={() => onSubmit(true)}
        onDismiss={onDismiss}
      />
    )
  );

  const changeAssetGroups = usePromiseModal<boolean, { assets: Asset[]; assetGroups: AssetGroup[] }>(
    ({ show, onSubmit, onDismiss, assets, assetGroups }) => (
      <AssetGroupsBulkModal
        assets={assets}
        assetGroups={assetGroups}
        show={show}
        onConfirm={() => onSubmit(true)}
        onDismiss={onDismiss}
      />
    )
  );

  const handleEditAsset = async (asset: Asset) => {
    const response = await editAsset.invoke({ asset, assetGroups });
    if (response === true) {
      toast.success('Image updated successfully');
      await refreshImages();
    }
  };

  const handleDeleteAssets = async (assets: Asset[]) => {
    const response = await deleteAssets.invoke({ assets });
    if (response === true) {
      toast.success(`${assets.length === 1 ? 'Image' : pluralize('image', assets.length, true)} deleted successfully`);
      setSelectedIds(ids => ids.filter(id => !assets.some(asset => asset.id === id)));
      await refreshImages();
    }
  };

  const handleSelectedChange = (asset: Asset, selected: boolean) => {
    setSelectedIds(ids => (selected ? [...ids.filter(id => id !== asset.id), asset.id] : ids.filter(id => id !== asset.id)));
  };

  const handleSelectAllChange = (selected: boolean) => {
    setSelectedIds(selected ? images.map(image => image.id) : []);
  };

  const handleSetHidden = async (assets: Asset[], hidden: boolean) => {
    setIsBulkUpdating(true);
    try {
      await APIbulkUpdateSprkImages({ action: hidden ? 'hide' : 'unhide', ids: assets.map(asset => asset.id) });
      toast.success(`${assets.length === 1 ? 'Image' : pluralize('image', assets.length, true)} ${hidden ? 'hidden' : 'unhidden'}`);
      await refreshImages();
    } catch (error) {
      toast.error((error as Error)?.message || `Failed to ${hidden ? 'hide' : 'unhide'} images`);
    } finally {
      setIsBulkUpdating(false);
    }
  };

  const handleChangeGroups = async (assets: Asset[]) => {
    const response = await changeAssetGroups.invoke({ assets, assetGroups });
    if (response === true) {
      toast.success(`Groups updated for ${pluralize('image', assets.length, true)}`);
      await refreshImages();
    }
  };

  const handleBulkAction = (action: BulkAction) => {
    if (selectedImages.length === 0) {
      return;
    }
    if (action === 'groups') {
      handleChangeGroups(selectedImages);
    }
    if (action === 'hide' || action === 'unhide') {
      handleSetHidden(selectedImages, action === 'hide');
    }
    if (action === 'delete') {
      handleDeleteAssets(selectedImages);
    }
  };

  const bulkActionHandlerRef = useRef(handleBulkAction);
  bulkActionHandlerRef.current = handleBulkAction;

  useEffect(() => {
    const dropdown = bulkDropdownRef.current;
    const handleSelect = (event: Event) => {
      const action = (event as CustomEvent<{ item: { value: BulkAction } }>).detail.item.value;
      bulkActionHandlerRef.current(action);
    };
    dropdown?.addEventListener('wa-select', handleSelect);
    return () => dropdown?.removeEventListener('wa-select', handleSelect);
  }, []);

  return (
    <>
      <div className="dashboard-header page-header wa-cluster">
        <div>
          <h2 className="page-title">SPRK <em>images</em></h2>
          <p>All images available to use in SPRKs</p>
        </div>
        <div>
          <wa-button
            size="s"
            onClick={() => navigate({ to: '/dashboard/admin/images/add' })}
          >
            Add images
          </wa-button>
        </div>
      </div>
      <div className="dashboard-admin dashboard-admin-users wa-stack wa-gap-0 inner">
        <div className="wa-grid">
          <div className="dashboard-admin-assets-bulk wa-cluster wa-gap-s">
            <wa-dropdown ref={(dropdown) => { bulkDropdownRef.current = dropdown; }} size="s" placement="bottom-start">
              <wa-button size="s" appearance="outlined" slot="trigger" withCaret disabled={selectedImages.length === 0 || isBulkUpdating}>
                {isBulkUpdating ? 'Updating...' : 'Bulk actions'}
              </wa-button>
              <wa-dropdown-item value="groups">
                <i className="fa-light fa-folder" slot="icon" />
                Change groups
              </wa-dropdown-item>
              <wa-dropdown-item value="hide">
                <i className="fa-light fa-eye-slash" slot="icon" />
                Hide
              </wa-dropdown-item>
              <wa-dropdown-item value="unhide">
                <i className="fa-light fa-eye" slot="icon" />
                Unhide
              </wa-dropdown-item>
              <wa-divider />
              <wa-dropdown-item value="delete" variant="danger">
                <i className="fa-light fa-trash" slot="icon" />
                Delete
              </wa-dropdown-item>
            </wa-dropdown>
            <span className="dashboard-admin-assets-bulk-count" aria-live="polite">
              {selectedImages.length > 0 ? `${pluralize('image', selectedImages.length, true)} selected` : 'Select images to use bulk actions'}
            </span>
            {selectedImages.length > 0 && (
              <wa-button size="s" appearance="plain" onClick={() => setSelectedIds([])}>
                Clear selection
              </wa-button>
            )}
          </div>
          <div className="total-items">
            <PaginationHeader text="image" total={totalImages} perPage={perPage} />
          </div>
        </div>
        <div className="dashboard-admin-users-list wa-stack">
          <table>
            <thead>
              <tr>
                <th className="select">
                  <wa-checkbox
                    className="wa-visually-hidden-label"
                    ref={(checkbox: WaCheckbox | null) => {
                      if (checkbox) {
                        checkbox.checked = isAllSelected;
                        checkbox.indeterminate = selectedImages.length > 0 && !isAllSelected;
                      }
                    }}
                    disabled={images.length === 0}
                    onChange={(event) => handleSelectAllChange((event.target as WaCheckbox).checked)}
                  >
                    Select all images on this page
                  </wa-checkbox>
                </th>
                <th>&nbsp;</th>
                <th>Title</th>
                <th>Groups</th>
                <th>Hidden</th>
                <th>SPRKs</th>
                <th className="actions">&nbsp;</th>
              </tr>
            </thead>
            <tbody>
              {images.map((image: Asset) => (
                <DashboardAdminAsset
                  key={image.id}
                  asset={image}
                  selected={selectedIds.includes(image.id)}
                  handleSelectedChange={handleSelectedChange}
                  handleToggleHidden={(asset) => handleSetHidden([asset], !asset.metadata?.hidden)}
                  handleEditAsset={handleEditAsset}
                  handleDeleteAsset={(asset) => handleDeleteAssets([asset])}
                />
              ))}
            </tbody>
          </table>
          <Pagination total={totalImages} perPage={perPage} />
        </div>
        {editAsset.modal}
        {deleteAssets.modal}
        {changeAssetGroups.modal}
      </div>
    </>
  );
}

export default DashboardAdminAssets;
